/**
 * ResourceSelectionModal - First step in the resource collection flow.
 * Allows users to select which materials they have (audio, English text, translation).
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalShell from './ModalShell';
import { TRANSLATION_LANGUAGES } from '../useResourceModalFlow';
import './ResourceSelectionModal.css';

interface ResourceSelectionModalProps {
  onSelect: (audio: boolean, english: boolean, translation: boolean, translationLanguage: string) => void;
  onClose: () => void;
  defaultLanguage?: string;
}

const ResourceSelectionModal: React.FC<ResourceSelectionModalProps> = ({
  onSelect,
  onClose,
  defaultLanguage,
}) => {
  const { t, i18n } = useTranslation(['creator', 'common']);

  const [hasAudio, setHasAudio] = useState(false);
  const [hasEnglish, setHasEnglish] = useState(false);
  const [hasTranslation, setHasTranslation] = useState(false);
  const [translationLanguage, setTranslationLanguage] = useState(
    defaultLanguage || i18n.language || 'zh-TW'
  );

  const nothingSelected = !hasAudio && !hasEnglish && !hasTranslation;

  const handleContinue = () => {
    onSelect(hasAudio, hasEnglish, hasTranslation, translationLanguage);
  };

  return (
    <ModalShell
      title={t('creator:workflow.resourceModal.selectionTitle')}
      onClose={onClose}
      onNext={handleContinue}
      nextLabel={t('creator:workflow.resourceModal.continue')}
      showClose
    >
      <div className="resource-selection-options">
        {/* Audio file option */}
        <div
          className={`resource-option ${hasAudio ? 'resource-option-active' : ''}`}
          onClick={() => setHasAudio(!hasAudio)}
          role="checkbox"
          aria-checked={hasAudio}
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setHasAudio(!hasAudio); } }}
        >
          <div className="resource-option-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
          </div>
          <div className="resource-option-text">
            <div className="resource-option-title">{t('creator:workflow.resourceModal.hasAudio')}</div>
            <div className="resource-option-desc">{t('creator:workflow.resourceModal.hasAudioDesc')}</div>
          </div>
          <div className="resource-option-checkbox">
            {hasAudio && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </div>
        </div>

        {/* English text option */}
        <div
          className={`resource-option ${hasEnglish ? 'resource-option-active' : ''}`}
          onClick={() => setHasEnglish(!hasEnglish)}
          role="checkbox"
          aria-checked={hasEnglish}
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setHasEnglish(!hasEnglish); } }}
        >
          <div className="resource-option-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
          </div>
          <div className="resource-option-text">
            <div className="resource-option-title">{t('creator:workflow.resourceModal.hasEnglish')}</div>
            <div className="resource-option-desc">{t('creator:workflow.resourceModal.hasEnglishDesc')}</div>
          </div>
          <div className="resource-option-checkbox">
            {hasEnglish && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </div>
        </div>

        {/* Translation text option */}
        <div
          className={`resource-option ${hasTranslation ? 'resource-option-active' : ''}`}
          onClick={() => setHasTranslation(!hasTranslation)}
          role="checkbox"
          aria-checked={hasTranslation}
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setHasTranslation(!hasTranslation); } }}
        >
          <div className="resource-option-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 5h7" />
              <path d="M9 3v2c0 4.418-2.239 8-5 8" />
              <path d="M5 9c0 2.144 2.952 3.908 6.7 4" />
              <path d="M12 20l4-9 4 9" />
              <path d="M13.5 17h5" />
            </svg>
          </div>
          <div className="resource-option-text">
            <div className="resource-option-title">{t('creator:workflow.resourceModal.hasTranslation')}</div>
            <div className="resource-option-desc">{t('creator:workflow.resourceModal.hasTranslationDesc')}</div>
          </div>
          <div className="resource-option-checkbox">
            {hasTranslation && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </div>
        </div>
      </div>

      {/* Translation language dropdown */}
      <div className="resource-selection-language">
        <label htmlFor="translation-language">
          {t('creator:workflow.resourceModal.translationLanguage')}
        </label>
        <select
          id="translation-language"
          value={translationLanguage}
          onChange={(e) => setTranslationLanguage(e.target.value)}
        >
          {TRANSLATION_LANGUAGES.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.flag} {lang.name}
            </option>
          ))}
        </select>
      </div>

      {/* AI generation hint when nothing selected */}
      {nothingSelected && (
        <div className="resource-selection-hint">
          <span className="resource-selection-hint-icon" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </span>
          <span>{t('creator:workflow.resourceModal.aiGenerateHint')}</span>
        </div>
      )}
    </ModalShell>
  );
};

export default ResourceSelectionModal;
